import type { ReactElement } from "react";

import type {
    SignalNameSegment,
    SignalNameSelectionOverflow,
} from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalNameSegments";
import type { SignalSelectionTooltipLabels } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalSelectionTooltipLabels";
import { getSignalDataTooltipLabels } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalSelectionTooltipLabels";
import { parseSignalDataSegment } from "app/pages/sales-intelligence/sub-modules/signals/utils/parseSignalDataSegment";
import { hasSignalNameSelectionOverflow } from "app/pages/sales-intelligence/sub-modules/signals/utils/signalNameSelectionOverflow";

import SignalsSegmentTranslatable from "./SignalsSegmentTranslatable";
import { StyledSignalDataLine, StyledText } from "./styles";

type Props = {
    signalData?: SignalNameSegment;
    selectionOverflowOn?: SignalNameSelectionOverflow;
    selectionTooltipLabels?: SignalSelectionTooltipLabels;
};

const SignalsSegmentedSignalData = ({
    signalData,
    selectionOverflowOn,
    selectionTooltipLabels,
}: Props): ReactElement | null => {
    if (!signalData) {
        return null;
    }

    const overflowTooltipLabels = getSignalDataTooltipLabels(selectionTooltipLabels);
    const parsed = parseSignalDataSegment(signalData);

    if (parsed.kind === "withTimeframe") {
        const showSelectionOverflowTooltip =
            (overflowTooltipLabels?.length ?? 0) > 1 &&
            hasSignalNameSelectionOverflow(selectionOverflowOn, "signalDataSelection");

        return (
            <StyledSignalDataLine data-automation="signal-segmented-data">
                <SignalsSegmentTranslatable
                    as="span"
                    data-automation="signal-segmented-selection"
                    overflowTooltipLabels={overflowTooltipLabels}
                    showOverflowTooltip={showSelectionOverflowTooltip}
                    translatable={parsed.selection}
                />{" "}
                <SignalsSegmentTranslatable
                    as="span"
                    translatable={parsed.timeframe}
                />
            </StyledSignalDataLine>
        );
    }

    const showDataOverflowTooltip =
        (overflowTooltipLabels?.length ?? 0) > 1 && hasSignalNameSelectionOverflow(selectionOverflowOn, "signalData");

    return (
        <SignalsSegmentTranslatable
            as={StyledText}
            overflowTooltipLabels={overflowTooltipLabels}
            showOverflowTooltip={showDataOverflowTooltip}
            translatable={parsed.segment}
        />
    );
};

export default SignalsSegmentedSignalData;
